import {
  PassportFields,
  PassportImages,
  SdkMessage,
  DocInfoAllInOneParam,
} from "./passportType";

export enum PassportReaderStatus {
  DISCONNECTED = "DISCONNECTED",
  CONNECTING = "CONNECTING",
  CONNECTED = "CONNECTED",
  INITIALIZING = "INITIALIZING",
  READY = "READY",
  ERROR = "ERROR",
  READING = "READING",
}

export enum PassportReaderMessage {
  CONNECT = "CONNECT",
  DISCONNECT = "DISCONNECT",
  DEVICE_STATUS = "DEVICE_STATUS",
  INIT_SUCCESS = "INIT_SUCCESS",
  INIT_FAILURE = "INIT_FAILURE",
  READ_START = "READ_START",
  READ_SUCCESS = "READ_SUCCESS",
  READ_ERROR = "READ_ERROR",
  DEVICE_CONNECTED = "DEVICE_CONNECTED",
  DEVICE_DISCONNECTED = "DEVICE_DISCONNECTED",
}

export interface PassportData {
  // Personal Information
  documentNumber: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  expiryDate: string;
  nationality: string;
  gender: string;
  issuingCountry: string;
  fullName?: string;

  // Document Type (passport, license, aadhar_card, etc.)
  documentType?: string;

  // Images - All possible image types from scanner
  photo?: string; // Head photo (OcrHead or ChipHead)
  signature?: string;
  whiteImage?: string; // White light document image
  irImage?: string; // Infrared document image
  uvImage?: string; // Ultraviolet document image
  ocrHeadImage?: string; // OCR extracted head photo
  chipHeadImage?: string; // Chip extracted head photo
  sidHeadImage?: string; // Chinese ID card head photo
  fullDocumentImage?: string; // Main document image

  // Additional fields that might come from scanner
  address?: string;
  placeOfBirth?: string;
  issuingAuthority?: string;
  personalNumber?: string;
}

export interface PassportReaderEvent {
  type: PassportReaderMessage;
  status: PassportReaderStatus;
  data?: PassportData;
  error?: string;
  timestamp: number;
}

class PassportReaderService {
  private ws: WebSocket | null = null;
  private status: PassportReaderStatus = PassportReaderStatus.DISCONNECTED;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: Map<string, Set<(event: PassportReaderEvent) => void>> =
    new Map();
  private reconnectTimeout?: NodeJS.Timeout;
  private heartbeatInterval?: NodeJS.Timeout;
  private pendingTextData: PassportFields | null = null; // Temporary storage for text data
  private pendingImages: PassportImages | null = null; // Temporary storage for images
  private dataMergeTimeout?: NodeJS.Timeout; // Timeout to merge partial data

  constructor() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  private handleVisibilityChange() {
    if (document.hidden) {
      this.stopHeartbeat();
    } else if (this.ws && this.status === PassportReaderStatus.READY) {
      this.startHeartbeat();
    }
  }

  connect(): void {
    if (
      this.ws &&
      (this.status === PassportReaderStatus.CONNECTING ||
        this.status === PassportReaderStatus.CONNECTED)
    ) {
      console.log("PassportReader: Already connected or connecting");
      return;
    }

    // Reset reconnect attempts when manually connecting
    this.reconnectAttempts = 0;

    this.status = PassportReaderStatus.CONNECTING;
    this.notifyListeners({
      type: PassportReaderMessage.CONNECT,
      status: this.status,
      timestamp: Date.now(),
    });

    // Determine protocol based on current page protocol
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const wsUrl =
      process.env.REACT_APP_PASSPORT_WS_URL ||
      `${protocol}://127.0.0.1:90/echo`;

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupWebSocketHandlers();
    } catch (error) {
      console.error(
        "PassportReader: Failed to create WebSocket connection:",
        error,
      );
      this.handleConnectionError();
    }
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log("PassportReader: WebSocket connection established");
      this.reconnectAttempts = 0;
      this.status = PassportReaderStatus.CONNECTED;

      this.notifyListeners({
        type: PassportReaderMessage.DEVICE_STATUS,
        status: this.status,
        timestamp: Date.now(),
      });

      this.initializeReader();
      this.startHeartbeat();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error("PassportReader: Failed to parse message:", error);
      }
    };

    this.ws.onerror = (error: Event) => {
      console.error("PassportReader: WebSocket error:", error);
      this.handleConnectionError();
    };

    this.ws.onclose = (event: CloseEvent) => {
      console.log(
        "PassportReader: WebSocket connection closed:",
        event.code,
        event.reason,
      );
      this.stopHeartbeat();

      if (this.status !== PassportReaderStatus.DISCONNECTED) {
        this.handleDisconnection();
      }
    };
  }

  private initializeReader(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    this.status = PassportReaderStatus.INITIALIZING;
    this.notifyListeners({
      type: PassportReaderMessage.DEVICE_STATUS,
      status: this.status,
      timestamp: Date.now(),
    });

    // Get device status first
    this.ws.send(
      JSON.stringify({
        Type: "Request",
        Command: "Get",
        Operand: "OnLineStatus",
      }),
    );
  }

  private handleMessage(message: SdkMessage): void {
    // Handle SDK protocol messages
    if (message.Type === "Reply") {
      if (message.Command === "Get" && message.Operand === "OnLineStatus") {
        if (message.Succeeded === "Y") {
          this.status = PassportReaderStatus.READY;
          this.notifyListeners({
            type: PassportReaderMessage.INIT_SUCCESS,
            status: this.status,
            timestamp: Date.now(),
          });
          // Set reading parameters
          this.setReadingParameters();
        } else {
          this.status = PassportReaderStatus.ERROR;
          this.notifyListeners({
            type: PassportReaderMessage.INIT_FAILURE,
            status: this.status,
            error: message.Result || "Device not online",
            timestamp: Date.now(),
          });
        }
      }
    } else if (message.Type === "Notify") {
      switch (message.Command) {
        case "Save":
          if (message.Operand === "CardContentText") {
            // Store text data and wait for images
            console.log("📄 Received CardContentText:", message.Param);
            if (
              message.Param &&
              typeof message.Param === "object" &&
              !Array.isArray(message.Param)
            ) {
              this.pendingTextData = message.Param as PassportFields;
              this.scheduleMergeData();
            } else {
              console.warn("Invalid text data format received");
            }
          } else if (message.Operand === "Images") {
            // Store images and wait for text data
            console.log(
              "🖼️ Received Images:",
              message.Param ? Object.keys(message.Param) : "No images",
            );
            if (
              message.Param &&
              typeof message.Param === "object" &&
              !Array.isArray(message.Param)
            ) {
              this.pendingImages = message.Param as PassportImages;
              this.scheduleMergeData();
            } else {
              console.warn("Invalid image data format received");
            }
          } else if (
            message.Operand === "DocInfoAllInOne" &&
            message.Param &&
            typeof message.Param === "object" &&
            "Fields" in message.Param
          ) {
            // Complete data with both fields and images
            console.log("📦 Received DocInfoAllInOne");
            const param = message.Param as DocInfoAllInOneParam;
            console.log("   Fields:", param.Fields);
            console.log(
              "   Images:",
              param.Images ? Object.keys(param.Images) : [],
            );
            const passportData = this.parsePassportData(
              param.Fields,
              param.Images,
            );
            this.clearPendingData();
            this.notifyListeners({
              type: PassportReaderMessage.READ_SUCCESS,
              status: PassportReaderStatus.READY,
              data: passportData,
              timestamp: Date.now(),
            });
          }
          break;
        case "Display":
          // Handle display messages (errors, notifications)
          if (message.Operand === "RealtimeMessage") {
            console.log("PassportReader: Message from service:", message.Param);
            if (
              message.Param &&
              typeof message.Param === "string" &&
              message.Param.includes("error")
            ) {
              this.notifyListeners({
                type: PassportReaderMessage.READ_ERROR,
                status: this.status,
                error: message.Param,
                timestamp: Date.now(),
              });
            }
          }
          break;
        case "CardDetected":
          console.log("PassportReader: Card detected");
          // Clear any pending data when new card is detected
          this.clearPendingData();
          break;
        case "CardTakenaway":
          console.log("PassportReader: Card removed");
          break;
      }
    }
  }

  private scheduleMergeData(): void {
    // Clear existing timeout
    if (this.dataMergeTimeout) {
      clearTimeout(this.dataMergeTimeout);
    }

    // Wait 500ms for both text and images to arrive
    this.dataMergeTimeout = setTimeout(() => {
      this.mergeAndNotifyData();
    }, 500);
  }

  private mergeAndNotifyData(): void {
    console.log("🔄 Merging data...");
    console.log("   Has text data:", !!this.pendingTextData);
    console.log("   Has images:", !!this.pendingImages);

    // If we have text data, parse it (with or without images)
    if (this.pendingTextData) {
      const passportData = this.parsePassportData(
        this.pendingTextData,
        this.pendingImages,
      );
      this.clearPendingData();
      this.notifyListeners({
        type: PassportReaderMessage.READ_SUCCESS,
        status: PassportReaderStatus.READY,
        data: passportData,
        timestamp: Date.now(),
      });
    }
  }

  private clearPendingData(): void {
    this.pendingTextData = null;
    this.pendingImages = null;
    if (this.dataMergeTimeout) {
      clearTimeout(this.dataMergeTimeout);
      this.dataMergeTimeout = undefined;
    }
  }

  private setReadingParameters(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    // Enable VIZ reading
    this.ws.send(
      JSON.stringify({
        Type: "Request",
        Command: "Set",
        Operand: "VIZ",
        Param: "Y",
      }),
    );

    // Enable chip reading
    this.ws.send(
      JSON.stringify({
        Type: "Request",
        Command: "Set",
        Operand: "RFID",
        Param: "Y",
      }),
    );
  }

  private parsePassportData(
    param: PassportFields,
    images?: PassportImages,
  ): PassportData {
    // Log raw data for debugging
    console.log("========== PASSPORT DATA PARSING ==========");
    console.log("Raw param data:", JSON.stringify(param, null, 2));
    console.log("Available field names:", Object.keys(param));
    if (images) {
      console.log("Available image types:", Object.keys(images));
    }
    console.log("==========================================");

    // Parse the SDK format data into our PassportData format
    const data: PassportData = {
      // Basic document information
      documentNumber:
        param["The passport number from MRZ"] ||
        param["Passport number"] ||
        param.DocumentNumber ||
        param.PassportNumber ||
        param["公民身份证号码"] ||
        param["证件号码"] ||
        param.IDNumber ||
        "",
      firstName:
        param["English first name"] ||
        param["Given Name(VIZ)"] ||
        param.FirstName ||
        param.Name?.split(" ")[0] ||
        "",
      lastName:
        param["English surname"] ||
        param["Surname(VIZ)"] ||
        param.LastName ||
        param.Surname ||
        param.Name?.split(" ").slice(1).join(" ") ||
        "",
      fullName:
        param["English name"] ||
        param["National name"] ||
        param.Name ||
        param["姓名"] ||
        `${param["English first name"] || param.FirstName || ""} ${param["English surname"] || param.LastName || ""}`.trim(),
      dateOfBirth:
        param["Date of birth"] ||
        param["Date of birth(OCR)"] ||
        param.DateOfBirth ||
        param.Birthday ||
        param["出生"] ||
        param["出生日期"] ||
        "",
      expiryDate:
        param["Date of expiry"] ||
        param["Date of expiry(OCR)"] ||
        param.ExpiryDate ||
        param.DateOfExpiry ||
        param["有效期至"] ||
        param.ValidUntil ||
        "",
      nationality:
        param.Nationality || param["Nationality code"] || param["国籍"] || "",
      gender:
        param.Sex || param["Sex(OCR)"] || param.Gender || param["性别"] || "",
      issuingCountry:
        param["Issuing country code"] ||
        param.IssuingCountry ||
        param.Country ||
        param.IssueCountry ||
        "",

      // Document type detection
      documentType: this.detectDocumentType(param),

      // Additional fields
      address: param.Address || param["住址"] || param.Residence || "",
      placeOfBirth: param.PlaceOfBirth || param.BirthPlace || "",
      issuingAuthority:
        param.IssuingAuthority ||
        param["签发机关"] ||
        param.Authority ||
        param.IssuedBy ||
        "",
      personalNumber: param.PersonalNumber || param.IDN || "",
    };

    // Parse images if provided
    if (images) {
      // White light image (main document image)
      data.whiteImage = images.White || images.white || "";

      // Infrared image
      data.irImage = images.IR || images.ir || "";

      // Ultraviolet image
      data.uvImage = images.UV || images.uv || "";

      // OCR head image
      data.ocrHeadImage = images.OcrHead || images.ocrHead || "";

      // Chip head image
      data.chipHeadImage = images.ChipHead || images.chipHead || "";

      // Chinese ID card head image
      data.sidHeadImage = images.SidHead || images.sidHead || "";

      // Set main photo to the first available head image
      data.photo =
        data.ocrHeadImage ||
        data.chipHeadImage ||
        data.sidHeadImage ||
        images.Photo ||
        "";

      // Signature
      data.signature = images.Signature || images.signature || "";

      // Set full document image to white light image if available
      data.fullDocumentImage = data.whiteImage || "";
    }

    return data;
  }

  private detectDocumentType(param: PassportFields): string {
    // Try to detect document type from field names or values
    if (param.DocumentType) {
      return this.normalizeDocumentType(param.DocumentType);
    }

    // Check CARD_NAME field (from scanner)
    const cardName = param.CARD_NAME?.toLowerCase() || "";
    if (cardName.includes("passport")) {
      return "passport";
    }
    if (cardName.includes("license") || cardName.includes("driver")) {
      return "license";
    }
    if (cardName.includes("id") || cardName.includes("identity")) {
      return "aadhar_card";
    }

    // Check Passport type field (P, D, S, etc.)
    if (param["Passport type"] === "P" || param["Passport type"]) {
      return "passport";
    }

    // Check for passport indicators
    if (
      param["The passport number from MRZ"] ||
      param["Passport number"] ||
      param.PassportNumber ||
      param.DocumentNumber?.startsWith("P") ||
      param.Type === "Passport"
    ) {
      return "passport";
    }

    // Check for Chinese ID card indicators
    if (param["公民身份证号码"] || param["姓名"] || param["民族"]) {
      return "aadhar_card"; // Using aadhar_card as generic ID card type
    }

    // Check for license indicators
    if (
      param.LicenseNumber ||
      param.DriverLicense ||
      param.Type === "License"
    ) {
      return "license";
    }

    // Check for voter card
    if (param.VoterID || param.Type === "VoterCard") {
      return "voter_card";
    }

    // Default to passport if cannot determine
    return "passport";
  }

  private normalizeDocumentType(type: string): string {
    const typeMap: Record<string, string> = {
      passport: "passport",
      Passport: "passport",
      PASSPORT: "passport",
      license: "license",
      License: "license",
      LICENSE: "license",
      "driver license": "license",
      "Driver License": "license",
      aadhar: "aadhar_card",
      Aadhar: "aadhar_card",
      "aadhar card": "aadhar_card",
      "Aadhar Card": "aadhar_card",
      voter: "voter_card",
      Voter: "voter_card",
      "voter card": "voter_card",
      "Voter Card": "voter_card",
      greenbook: "green_book",
      "Green Book": "green_book",
      refugee: "refugee_card",
      Refugee: "refugee_card",
      "refugee card": "refugee_card",
      "Refugee Card": "refugee_card",
    };

    return typeMap[type] || "passport";
  }

  private handleConnectionError(): void {
    this.status = PassportReaderStatus.ERROR;
    const errorMessage =
      this.reconnectAttempts >= this.maxReconnectAttempts
        ? "Passport reader service is not available. Please ensure the service is running on port 90."
        : "Connection error. Retrying...";

    this.notifyListeners({
      type: PassportReaderMessage.DEVICE_STATUS,
      status: this.status,
      error: errorMessage,
      timestamp: Date.now(),
    });

    this.scheduleReconnect();
  }

  private handleDisconnection(): void {
    this.status = PassportReaderStatus.DISCONNECTED;
    this.notifyListeners({
      type: PassportReaderMessage.DISCONNECT,
      status: this.status,
      timestamp: Date.now(),
    });

    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("PassportReader: Max reconnect attempts reached");
      this.status = PassportReaderStatus.ERROR;
      this.notifyListeners({
        type: PassportReaderMessage.DEVICE_STATUS,
        status: this.status,
        error:
          "Cannot connect to passport reader service. Please check:\n1. Service is installed and running\n2. Service is listening on port 90\n3. Firewall allows local connections",
        timestamp: Date.now(),
      });
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000,
    );
    this.reconnectAttempts++;

    console.log(
      `PassportReader: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Check device status periodically
        this.ws.send(
          JSON.stringify({
            Type: "Request",
            Command: "Get",
            Operand: "OnLineStatus",
          }),
        );
      }
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = undefined;
    }
  }

  readPassport(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error("PassportReader: WebSocket not connected");
      this.notifyListeners({
        type: PassportReaderMessage.READ_ERROR,
        status: this.status,
        error: "Not connected to passport reader service",
        timestamp: Date.now(),
      });
      return;
    }

    if (this.status !== PassportReaderStatus.READY) {
      console.error("PassportReader: Reader not ready");
      this.notifyListeners({
        type: PassportReaderMessage.READ_ERROR,
        status: this.status,
        error: "Passport reader not ready",
        timestamp: Date.now(),
      });
      return;
    }

    this.notifyListeners({
      type: PassportReaderMessage.READ_START,
      status: PassportReaderStatus.READING,
      timestamp: Date.now(),
    });

    // Send manual trigger command as per SDK
    this.ws.send(
      JSON.stringify({
        Type: "Notify",
        Command: "Trigger",
        Operand: "ManualRecog",
        Param: 5, // 5 second timeout
      }),
    );
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }

    this.stopHeartbeat();
    this.clearPendingData();

    if (this.ws) {
      this.status = PassportReaderStatus.DISCONNECTED;
      this.ws.close(1000, "User initiated disconnect");
      this.ws = null;
    }

    this.notifyListeners({
      type: PassportReaderMessage.DISCONNECT,
      status: PassportReaderStatus.DISCONNECTED,
      timestamp: Date.now(),
    });
  }

  addEventListener(listener: (event: PassportReaderEvent) => void): () => void {
    const id = Math.random().toString(36).substring(2, 15);

    if (!this.listeners.has(id)) {
      this.listeners.set(id, new Set());
    }

    this.listeners.get(id)?.add(listener);

    return () => {
      this.listeners.get(id)?.delete(listener);
      if (this.listeners.get(id)?.size === 0) {
        this.listeners.delete(id);
      }
    };
  }

  private notifyListeners(event: PassportReaderEvent): void {
    this.listeners.forEach((listenerSet) => {
      listenerSet.forEach((listener) => {
        try {
          listener(event);
        } catch (error) {
          console.error("PassportReader: Error in event listener:", error);
        }
      });
    });
  }

  getStatus(): PassportReaderStatus {
    return this.status;
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  isReady(): boolean {
    return this.status === PassportReaderStatus.READY;
  }

  cleanup(): void {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    this.disconnect();
    this.listeners.clear();
  }
}

export const passportReaderService = new PassportReaderService();
